<template>
  <a-modal :visible="show" @cancel="$emit('close')">
    <h3 slot="title">
      <a-icon class="mr-2" type="cloud-upload" />
      {{ $t('msg.account.import.title') }}
    </h3>
    <!-- Form -->
    <div class="mb-2">
      {{ $t('msg.account.import.subtitle') }}
      <a-tooltip placement="right" :title="$t('msg.account.import.warning')" v-if="blocked">
        <a-icon type="exclamation-circle" class="text-danger font-size-14 ml-1" />
      </a-tooltip>
    </div>
    <!-- Upload -->
    <a-upload name="rates" :multiple="false" v-model="fileList" :file-list="fileList" :before-upload="beforeUpload"
      @change="handleChange" @remove="handleRemove" accept=".csv">
      <a-button :disabled="importDisabled">
        <a-icon type="upload" /> Click to Upload
      </a-button>
    </a-upload>
    <br />
    <div class="mb-2">
      <a-alert v-if="(fileList.length > 0 &&
        errorMessages.length === 0) ||
        showSuccess" type="success" show-icon :message="$t('msg.account.import.success')">
      </a-alert>
      <a-alert v-if="(importDisabled &&
        errorMessages.length > 0) ||
        showError" type="error" show-icon :message="$t('msg.account.import.error.generic')"
        :description="errorMessages.join('\n')">
      </a-alert>
    </div>
    <!-- Actions -->
    <div slot="footer" class="p-3">
      <a-button key="back" @click="onCancel">
        {{ $t('cancel') }}
      </a-button>
      <a-button key="submit" type="primary" :loading="loading" :disabled="blocked" @click="onConfirm">
        {{ $t('import') }}
      </a-button>
    </div>
  </a-modal>
</template>
<script>
import lms from '@/api/lms'

export default {
  props: ['show'],
  data() {
    return {
      loading: false,
      selectedFile: null,
      fileList: [],
      errorMessages: [],
      importDisabled: false,
      showSuccess: false,
      showError: false,
    }
  },
  computed: {
    visible() {
      return this.show
    },
    blocked() {
      return (
        this.selectedFile == null ||
        this.errorMessages == null ||
        this.fileList.length === 0 ||
        this.errorMessages.length > 0
      )
    },
  },
  methods: {
    beforeUpload(file) {
      this.errorMessages = []
      this.showSuccess = false
      this.showError = false
      const isLt10M = file.size / 1024 / 1024 > 10

      if (isLt10M) {
        this.errorMessages = this.$t('msg.account.import.error.size')
        return
      }

      const isCsv = file.type === 'text/csv'
      if (!isCsv) {
        this.errorMessages = this.$t('msg.account.import.error.type')
        return
      }

      if (file.size === 0) {
        this.errorMessages = this.$t('msg.account.import.error.empty')
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target.result
        const validContent = content.trim().length > 0 && content.split('\n').length > 1

        if (!validContent) {
          this.errorMessages = this.$t('msg.account.import.error.empty')
          return false
        }

        const validationResult = this.validateCSV(content)
        if (!validationResult.isValid) {
          this.errorMessages = validationResult.errors
          return false
        }
      }
      reader.readAsText(file)

      if (this.errorMessages.length > 0) {
        return false
      }

      this.selectedFile = file
      this.fileList = [file]
      return false // Prevent auto upload
    },

    handleChange(info) {
      if (info.fileList.length > 1) {
        info.fileList = info.fileList.slice(-1) // Keep only the last file
      }
      this.fileList = info.fileList
      this.importDisabled = this.fileList.length > 0

      if (this.errorMessages.length > 0) {
        this.handleRemove(info)
      }
    },

    handleRemove(file) {
      this.fileList = this.fileList.filter(f => f.uid !== file.uid)
      this.importDisabled = this.fileList.length > 0
    },

    onConfirm() {
      if (!this.selectedFile) {
        this.errorMessages = [this.$t('msg.account.import.error.confirm')]
        return
      }
      this.loading = true

      lms.account.importRates(this.selectedFile)
        .then(() => {
          this.errorMessages = []
        }).catch(e => {
          if (e.response?.data?.code === 'INVALID_REQUEST') {
            this.errorMessages = e.response.data.errors
          }
        })
        .finally(() => {
          this.$router.push('/accounts/imported')
        })
    },

    onCancel() {
      this.fileList = []
      this.importDisabled = false
      this.showError = false
      this.showSuccess = false
      this.$emit('close')
    },

    validateCSV(data) {
      const rows = data.split('\n')
      const headers = rows[0].split(',').map(col => col.trim())
      const errors = []
      const expectedHeaders = [
        'Account ID',
        'Account Name',
        'Rate ID',
        'Rate Method',
        'Rate Code',
        'Rate Currency',
        'Rate Value',
      ]

      if (headers.length !== expectedHeaders.length || !headers.every((header, index) => header === expectedHeaders[index])) {
        errors.push(this.$t('msg.account.import.error.header'))
      }

      if (errors.length > 0) {
        return {
          isValid: false,
          errors,
        }
      }
      const validRateMethods = ['EXPRESS', 'STANDARD']
      const validRateCurrencies = ['ARS', 'USD']

      for (let i = 1; i < rows.length; i++) {
        const columns = rows[i].split(',').map(col => col.trim())

        // Ignore rows with empty or null values
        if (columns.every(col => !col)) {
          continue
        }

        const [accountId, , rateId, rateMethod, rateCode, rateCurrency, rateValue] = columns

        if (isNaN(accountId) || Number(accountId) <= 0) {
          errors.push(this.$t('msg.account.import.row') + ` ${i + 1}:` + this.$('msg.CSVImport.header.accountId') + this.$t('msg.account.import.error.number'))
        }
        if (isNaN(rateId) || Number(rateId) <= 0) {
          errors.push(this.$t('msg.account.import.row') + ` ${i + 1}:` + this.$('msg.CSVImport.header.rateId') + this.$t('msg.account.import.error.number'))
        }
        if (isNaN(rateCode) || Number(rateCode) < 0) {
          errors.push(this.$t('msg.account.import.row') + ` ${i + 1}:` + this.$('msg.CSVImport.header.rateCode') + this.$t('msg.account.import.error.number'))
        }
        if (isNaN(rateValue) || Number(rateValue) <= 0) {
          errors.push(this.$t('msg.account.import.row') + ` ${i + 1}:` + this.$('msg.CSVImport.header.rateValue') + this.$t('msg.account.import.error.number'))
        }
        if (!validRateMethods.includes(rateMethod)) {
          errors.push(this.$t('msg.account.import.row') + ` ${i + 1}:` + this.$('msg.CSVImport.header.rateMethod') + this.$t('msg.account.import.error.invalid') + validRateMethods.join(', '))
        }
        if (!validRateCurrencies.includes(rateCurrency)) {
          errors.push(this.$t('msg.account.import.row') + ` ${i + 1}:` + this.$('msg.CSVImport.header.rateCurrency') + this.$t('msg.account.import.error.invalid') + validRateCurrencies.join(', '))
        }
      }
      return {
        isValid: errors.length === 0,
        errors,
      }
    },
  },
}

</script>
